import type { CommissionRule, MetaMensal } from '../../../../backend/src/domain/comissao/regras';
import { regrasGeraisDoSegmento } from '../../../../backend/src/domain/comissao/calcComissaoEspelhoExcel';
export type { CommissionRule, MetaMensal } from '../../../../backend/src/domain/comissao/regras';
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { format, startOfMonth } from 'date-fns';

export interface CommissionRuleRenovacaoDelta {
  id: string;
  commission_rule_id: string;
  faixa_delta: number;
  percentual: number;
  created_at: string;
  updated_at: string;
}

export function useCommissionRulesRenovacaoDelta(ruleIds: string[]) {
  const query = useQuery({
    queryKey: ['commission-rules-renovacao-delta', 'todas'],
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commission_rule_renovacao_delta' as never)
        .select('*')
        .order('faixa_delta', { ascending: true });
      if (error) throw error;
      return (data || []) as CommissionRuleRenovacaoDelta[];
    },
  });
  const ids = new Set(ruleIds.filter(Boolean));
  const data = query.data?.filter((regra) => ids.has(regra.commission_rule_id));
  return { ...query, data };
}

export interface MetaConfig {
  id: string;
  cargo: 'CONSULTOR' | 'SUPERVISOR' | 'COORDENADOR' | 'GERENTE';
  meta_altas: number;
  meta_fibras: number;
  multiplicador_n: boolean;
  ativo: boolean;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  created_at: string;
}

/**
 * TODAS as regras ativas de uma competência, em UMA consulta compartilhada.
 *
 * (Douglas 29/07 — "ainda muito lento para carregar página".) O seletor de
 * pessoas renderiza um item por funcionário e cada um chamava
 * `useCommissionRules(cargo, comp, usuarioId)`. Como o `usuarioId` entra na
 * chave, cada pessoa virava uma query própria — e, pra quem NÃO tem regra
 * individual (a maioria), eram DUAS em série: a individual vazia e depois o
 * fallback do cargo. Com dezenas de pessoas na lista isso sozinho já era
 * ~80 idas ao banco.
 *
 * Buscando tudo de uma vez, a resolução individual → cargo vira filtro em
 * memória. Mesma prioridade, mesmos dados, uma requisição.
 *
 * A chave começa com 'commission-rules' de propósito: as mutações invalidam
 * por esse prefixo, então continuam limpando este cache também.
 */
function useTodasCommissionRules(competenciaStr: string | null) {
  return useQuery({
    queryKey: ['commission-rules', 'todas-da-competencia', competenciaStr],
    enabled: !!competenciaStr,
    staleTime: 30 * 1000,
    placeholderData: (prev: CommissionRule[] | undefined) => prev,
    queryFn: async () => {
      let q = supabase
        .from('commission_rules')
        .select('*')
        .eq('ativo', true)
        .order('faixa_faturamento', { ascending: true });
      if (competenciaStr) q = q.eq('competencia', competenciaStr);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as CommissionRule[];
    },
  });
}

// Hook para buscar regras de comissão de um cargo em um mês específico
// Se usuarioId for fornecido, busca regra individual primeiro; fallback para regra do cargo
/** Segmento de cada equipe (CPF × CNPJ) — para a tela escolher a tabela de % do segmento da pessoa. */
export function useSegmentosDasEquipes() {
  const { data } = useQuery({
    queryKey: ['equipes-segmento'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from('equipes').select('id, nome, tipo_pessoa');
      if (error) throw error;
      return (data || []) as Array<{ id: string; nome: string; tipo_pessoa: string | null }>;
    },
  });
  return useMemo(() => {
    if (!data) return null;
    const seg = (t: string | null) => (t === 'PF' ? 'PF' : 'PJ') as 'PF' | 'PJ';
    return {
      porId: new Map(data.map((e) => [e.id, seg(e.tipo_pessoa)])),
      porNome: new Map(data.map((e) => [e.nome, seg(e.tipo_pessoa)])),
    };
  }, [data]);
}

export function useSegmentoDaEquipe(equipeId: string | null | undefined): 'PF' | 'PJ' {
  const segmentos = useSegmentosDasEquipes();
  return (equipeId && segmentos?.porId.get(equipeId)) || 'PJ';
}

export function useCommissionRules(cargo?: string, competencia?: Date, usuarioId?: string, segmento: 'PF' | 'PJ' = 'PJ') {
  const competenciaStr = competencia ? format(startOfMonth(competencia), 'yyyy-MM-dd') : null;

  // Caminho rápido: com competência definida, resolve a partir do lote único.
  const { data: todas, isLoading: todasLoading } = useTodasCommissionRules(competenciaStr);
  const resolvidas = useMemo(() => {
    if (!competenciaStr || !todas) return undefined;
    if (usuarioId) {
      const individual = todas.filter(r => r.usuario_id === usuarioId);
      if (individual.length > 0) return individual;
    }
    return regrasGeraisDoSegmento(todas, cargo, segmento);
  }, [todas, cargo, usuarioId, competenciaStr, segmento]);

  const legado = useQuery({
    // Sem competência não dá pra usar o lote (ele é por competência) — mantém o
    // caminho antigo, que é o único chamador nessa situação.
    enabled: !competenciaStr,
    queryKey: ['commission-rules', cargo, competenciaStr, usuarioId ?? null],
    queryFn: async () => {
      // 1. Se usuarioId fornecido, busca regra individual primeiro
      if (usuarioId && competenciaStr) {
        const { data: individual, error: errInd } = await supabase
          .from('commission_rules')
          .select('*')
          .eq('usuario_id', usuarioId)
          .eq('competencia', competenciaStr)
          .eq('ativo', true)
          .order('faixa_faturamento', { ascending: true });

        if (!errInd && individual && individual.length > 0) {
          return individual as CommissionRule[];
        }
      }

      // 2. Fallback: regras do cargo (sem usuario_id)
      let query = supabase
        .from('commission_rules')
        .select('*')
        .eq('ativo', true)
        .is('usuario_id', null)
        .order('faixa_faturamento', { ascending: true });

      if (cargo) {
        query = query.eq('cargo', cargo);
      }

      if (competenciaStr) {
        query = query.eq('competencia', competenciaStr);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CommissionRule[];
    },
    staleTime: 30 * 1000,
    placeholderData: (prev: any) => prev,
  });

  // Com competência: lote. Sem: caminho legado. Mesmo formato de retorno.
  if (competenciaStr) {
    return { ...legado, data: resolvidas, isLoading: todasLoading } as typeof legado;
  }
  return legado;
}

// Hook para buscar regras de comissão de um cargo para o cálculo (usa o mês de competência)
export function useCommissionRulesForCalculo(cargo?: string, competencia?: Date, usuarioId?: string) {
  return useCommissionRules(cargo, competencia, usuarioId);
}

// Hook para buscar configuração de metas
export function useMetasConfig(cargo?: string) {
  return useQuery({
    queryKey: ['metas-config', cargo],
    queryFn: async () => {
      let query = supabase
        .from('metas_config')
        .select('*')
        .eq('ativo', true);

      if (cargo) {
        query = query.eq('cargo', cargo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as MetaConfig[];
    },
    staleTime: 30 * 1000,
    placeholderData: (prev: any) => prev,
  });
}

// Hook para atualizar regra de comissão (master only)
export function useUpdateCommissionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: Partial<CommissionRule> & { id: string }) => {
      const { id, ...updates } = rule;
      const { data, error } = await supabase
        .from('commission_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-rules'] });
      toast({
        title: 'Regra atualizada',
        description: 'A regra de produtividade foi atualizada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============ METAS MENSAIS ============

// Hook para buscar metas mensais (com suporte a usuarioId individual OU equipeId).
// Prioridade na edição: individual (usuarioId) > equipe (equipeId) > cargo genérico.
export function useMetasMensais(cargo?: string, usuarioId?: string, equipeId?: string, tipoPessoa: 'PF' | 'PJ' = 'PJ') {
  return useQuery({
    queryKey: ['metas-mensais', cargo, usuarioId ?? null, equipeId ?? null, tipoPessoa],
    queryFn: async () => {
      if (usuarioId) {
        // Busca metas individuais do usuário
        const { data: individual } = await supabase
          .from('metas_mensais')
          .select('*')
          .eq('usuario_id', usuarioId)
          .order('competencia', { ascending: false });
        if (individual && individual.length > 0) return individual as MetaMensal[];
      }

      if (equipeId) {
        // Metas da equipe (cargo IS NULL, usuario_id IS NULL)
        const { data: porEquipe, error: errEq } = await supabase
          .from('metas_mensais')
          .select('*')
          .eq('equipe_id', equipeId)
          .order('competencia', { ascending: false });
        if (errEq) throw errEq;
        return (porEquipe || []) as MetaMensal[];
      }

      // Fallback: metas do cargo (sem usuario_id e sem equipe_id)
      let query = supabase
        .from('metas_mensais')
        .select('*')
        .is('usuario_id', null)
        .is('equipe_id', null)
        // Meta geral por cargo é por segmento: CPF e CNPJ têm a sua.
        .eq('tipo_pessoa', tipoPessoa)
        .order('competencia', { ascending: false });
      if (cargo) query = query.eq('cargo', cargo);
      const { data, error } = await query;
      if (error) throw error;
      return data as MetaMensal[];
    },
    staleTime: 30 * 1000,
    placeholderData: (prev: any) => prev,
  });
}

/**
 * TODAS as metas ativas de uma competência, em UMA consulta compartilhada.
 * Mesmo motivo do `useTodasCommissionRules`: com o `usuarioId` na chave, cada
 * item do seletor virava 1-2 queries próprias.
 *
 * Prefixo 'meta-mensal' preservado pra continuar sendo invalidado junto.
 */
function useTodasMetasMensais(competenciaStr: string) {
  return useQuery({
    queryKey: ['meta-mensal', 'todas-da-competencia', competenciaStr],
    staleTime: 30 * 1000,
    placeholderData: (prev: MetaMensal[] | undefined) => prev,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('metas_mensais')
        .select('*')
        .eq('competencia', competenciaStr)
        .eq('ativo', true);
      if (error) throw error;
      return (data || []) as MetaMensal[];
    },
  });
}

// Hook para buscar meta de um cargo em um mês específico (com suporte a usuarioId)
export function useMetaMensal(cargo: string, competencia: Date, usuarioId?: string, tipoPessoa: 'PF' | 'PJ' = 'PJ') {
  const competenciaStr = format(startOfMonth(competencia), 'yyyy-MM-dd');
  const { data: todas, isLoading, ...resto } = useTodasMetasMensais(competenciaStr);

  // Mesma prioridade de antes: individual do usuário → meta do cargo.
  const meta = useMemo(() => {
    if (!todas) return undefined;
    if (usuarioId) {
      const individual = todas.find(m => m.usuario_id === usuarioId);
      if (individual) return individual;
    }
    return todas.find(m => m.cargo === cargo && !m.usuario_id && (m.tipo_pessoa ?? 'PJ') === tipoPessoa) ?? null;
  }, [todas, cargo, usuarioId, tipoPessoa]);

  return { ...resto, data: meta, isLoading };
}

// Upsert meta mensal (com suporte a usuarioId individual)
// A META GERAL VALE PARA TODOS OS CONSULTORES (Douglas 30/09/2026). A meta de
// equipe ganha da geral e é replicada sozinha todo mês; sem isto, mudar a
// "Meta geral (todos os consultores)" não chegava a ninguém das equipes. O que
// muda na geral vai para a meta de cada equipe do mesmo mês e segmento; a
// exceção de uma equipe se ajusta depois, na própria equipe.
const CAMPOS_DA_META = [
  'meta_altas', 'meta_fibras', 'meta_fat_vn', 'meta_fat_total', 'meta_qtd_tt', 'meta_qtd_mp',
  'meta_qtd_migracao', 'meta_fat_ti', 'meta_fat_avancada', 'meta_sd', 'meta_tv', 'meta_fibras_renovacao',
  'usar_meta_altas', 'usar_meta_fibras', 'usar_meta_fat_vn', 'usar_meta_fat_total', 'usar_meta_qtd_tt',
  'usar_meta_qtd_mp', 'usar_meta_qtd_migracao', 'usar_meta_fat_ti', 'usar_meta_fat_avancada', 'usar_meta_sd', 'usar_meta_tv',
  'usar_meta_fibras_renovacao', 'usar_meta_migracao_junta', 'pct_meta_migracao_junta',
] as const;

async function levarMetaGeralParaAsEquipes(meta: Record<string, unknown>, tipoPessoa: string) {
  const mudou = Object.fromEntries(CAMPOS_DA_META.filter((c) => c in meta).map((c) => [c, meta[c]]));
  if (Object.keys(mudou).length === 0) return;
  const { data: linhas, error } = await supabase
    .from('metas_mensais')
    .select('id, equipe_id, usuario_id')
    .eq('competencia', meta.competencia as string)
    .eq('tipo_pessoa', tipoPessoa);
  if (error) throw error;
  const equipes = ((linhas || []) as Array<{ id: string; equipe_id: string | null; usuario_id: string | null }>)
    .filter((l) => l.equipe_id && !l.usuario_id);
  for (const l of equipes) {
    const { error: e } = await supabase.from('metas_mensais').update(mudou as never).eq('id', l.id);
    if (e) throw e;
  }
}

export function useUpsertMetaMensal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (meta: Partial<MetaMensal> & { cargo?: string | null; competencia: string; usuario_id?: string | null; equipe_id?: string | null }) => {
      if (meta.equipe_id) {
        // Meta de EQUIPE: cargo e usuario_id ficam NULL. Chave (equipe_id, competencia).
        const { cargo, usuario_id, ...metaEquipe } = meta;

        const { data: existing } = await supabase
          .from('metas_mensais')
          .select('id')
          .eq('equipe_id', meta.equipe_id)
          .eq('competencia', meta.competencia)
          .maybeSingle();

        if (existing) {
          const { data, error } = await supabase
            .from('metas_mensais')
            .update(metaEquipe)
            .eq('id', existing.id)
            .select()
            .single();
          if (error) throw error;
          return data;
        } else {
          const { data, error } = await supabase
            .from('metas_mensais')
            .insert({ ...metaEquipe, cargo: null, usuario_id: null })
            .select()
            .single();
          if (error) throw error;
          return data;
        }
      } else if (meta.usuario_id) {
        // Remover cargo para não conflitar com constraint cargo+competencia
        const { cargo, ...metaSemCargo } = meta;

        const { data: existing } = await supabase
          .from('metas_mensais')
          .select('id')
          .eq('usuario_id', meta.usuario_id)
          .eq('competencia', meta.competencia)
          .maybeSingle();

        if (existing) {
          const { data, error } = await supabase
            .from('metas_mensais')
            .update(metaSemCargo)
            .eq('id', existing.id)
            .select()
            .single();
          if (error) throw error;
          return data;
        } else {
          const { data, error } = await supabase
            .from('metas_mensais')
            .insert({ ...metaSemCargo, cargo: null })
            .select()
            .single();
          if (error) throw error;
          return data;
        }
      } else {
        const tipoPessoa = meta.tipo_pessoa ?? 'PJ';
        const { data, error } = await supabase
          .from('metas_mensais')
          .upsert({ ...meta, tipo_pessoa: tipoPessoa }, { onConflict: 'cargo,competencia,tipo_pessoa' })
          .select()
          .single();
        if (error) throw error;
        if (meta.cargo === 'CONSULTOR') await levarMetaGeralParaAsEquipes(meta, tipoPessoa);
        return data;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['metas-mensais'] });
      queryClient.invalidateQueries({ queryKey: ['meta-mensal', vars.cargo, vars.competencia] });
      toast({ title: 'Meta salva', description: 'Configuração de meta atualizada com sucesso.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao salvar meta', description: error.message, variant: 'destructive' });
    },
  });
}

// Hook para atualizar meta (master only)
export function useUpdateMetaConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meta: Partial<MetaConfig> & { id: string }) => {
      const { id, ...updates } = meta;
      const { data, error } = await supabase
        .from('metas_config')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metas-config'] });
      toast({
        title: 'Meta atualizada',
        description: 'A configuração de meta foi atualizada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export { FAIXAS_FATURAMENTO, encontrarFaixa, encontrarFaixaSupervisor } from '../../../../backend/src/domain/comissao/regras';
